import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { XCircle, AlertTriangle, History, RotateCcw } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PaymentReversalModal } from './PaymentReversalModal';
import { formatCurrency } from '@/lib/utils';

interface PaymentHistoryProps {
  invoice: {
    id: string;
    invoice_number: string;
    total_amount: number;
    paid_amount: number;
    customer_id: string;
    estimate_id: string;
  };
}

interface Payment {
  id: string;
  payment_date: string;
  payment_amount: number;
  payment_method: string | null;
  notes: string | null;
  is_cancelled: boolean | null;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  credit_application_id: string | null;
}

interface WriteOff {
  id: string;
  amount: number;
  reason: string;
  written_off_at: string;
}

export const PaymentHistory: React.FC<PaymentHistoryProps> = ({ invoice }) => {
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

  // Fetch payments
  const { data: payments, isLoading: paymentsLoading } = useQuery({
    queryKey: ['invoice-payments', invoice.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoice_payments')
        .select('*')
        .eq('invoice_id', invoice.id)
        .order('payment_date', { ascending: false });

      if (error) throw error;
      return data as Payment[];
    }
  });

  // Fetch write-offs
  const { data: writeOffs, isLoading: writeOffsLoading } = useQuery({
    queryKey: ['write-offs', invoice.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('write_offs')
        .select('*')
        .eq('invoice_id', invoice.id)
        .order('written_off_at', { ascending: false });

      if (error) throw error;
      return data as WriteOff[];
    }
  });

  const isLoading = paymentsLoading || writeOffsLoading;
  const hasHistory = (payments && payments.length > 0) || (writeOffs && writeOffs.length > 0);

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground py-4">
        Loading payment history...
      </div>
    );
  }

  if (!hasHistory) {
    return null;
  }

  return (
    <div className="mt-4 pt-4 border-t">
      <h4 className="font-medium flex items-center gap-2 mb-3">
        <History className="h-4 w-4" />
        Payment History
      </h4>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Method</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Payments */}
            {payments?.map((payment) => {
              // Check if this payment resulted in a credit (overpayment)
              const invoiceTotal = invoice.total_amount || 0;
              const paymentExceedsTotal = payment.payment_amount > invoiceTotal;
              const creditedAmount = paymentExceedsTotal ? payment.payment_amount - invoiceTotal : 0;
              
              return (
                <TableRow 
                  key={payment.id}
                  className={payment.is_cancelled ? 'opacity-60' : ''}
                >
                  <TableCell>
                    {format(new Date(payment.payment_date), 'dd MMM yyyy')}
                  </TableCell>
                  <TableCell>
                    {payment.credit_application_id ? (
                      <Badge variant="secondary">Credit Applied</Badge>
                    ) : (
                      <Badge variant="outline">Payment</Badge>
                    )}
                  </TableCell>
                  <TableCell>{payment.payment_method || '-'}</TableCell>
                  <TableCell className={`text-right ${payment.is_cancelled ? 'line-through' : ''}`}>
                    <div>
                      {formatCurrency(payment.payment_amount || 0)}
                      {creditedAmount > 0 && !payment.is_cancelled && (
                        <span className="block text-xs text-muted-foreground">
                          ({formatCurrency(creditedAmount)} credited)
                        </span>
                      )}
                    </div>
                  </TableCell>
                <TableCell>
                  {payment.is_cancelled ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Badge variant="destructive" className="gap-1">
                            <XCircle className="h-3 w-3" />
                            Cancelled
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="font-medium">Reason:</p>
                          <p>{payment.cancelled_reason || 'No reason provided'}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {payment.cancelled_at && format(new Date(payment.cancelled_at), 'dd MMM yyyy HH:mm')}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <Badge variant="default" className="bg-green-600">Active</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {!payment.is_cancelled && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedPayment(payment)}
                      className="text-destructive hover:text-destructive"
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Reverse
                    </Button>
                  )}
                </TableCell>
              </TableRow>
              );
            })}

            {/* Write-offs */}
            {writeOffs?.map((writeOff) => (
              <TableRow key={writeOff.id} className="bg-amber-50/50 dark:bg-amber-950/20">
                <TableCell>
                  {format(new Date(writeOff.written_off_at), 'dd MMM yyyy')}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Write-off
                  </Badge>
                </TableCell>
                <TableCell>-</TableCell>
                <TableCell className="text-right">
                  {formatCurrency(writeOff.amount || 0)}
                </TableCell>
                <TableCell>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Badge variant="secondary">Recorded</Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="font-medium">Reason:</p>
                        <p>{writeOff.reason}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
                <TableCell className="text-right">
                  {/* Write-offs cannot be reversed */}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Reversal Modal */}
      {selectedPayment && (
        <PaymentReversalModal
          open={!!selectedPayment}
          onOpenChange={(open) => !open && setSelectedPayment(null)}
          payment={selectedPayment}
          invoice={invoice}
        />
      )}
    </div>
  );
};
